import { useEffect, RefObject } from 'react';
import * as d3 from 'd3';
import { Theme } from '@mui/material';
import { MarketMetadata, MarketAnalysisData, SeriesVisibility, VolumeVisibility, AggregatedVolumePoint } from '../../types';
import { ChartLayout, ChartScales, ParsedChartPoint, RenderContext } from '../types';
import {
    renderGridLines,
    renderYAxis,
    renderXAxis,
    renderEmptyAxes,
    renderVolumeYAxis,
    renderFullSeriesMode,
    renderSimpleMode,
    renderVolumePanel,
} from '../renderers';
import { createTooltipElements, createTooltipHandlers, setupBrush, setupInteractiveOverlay } from '../utils';

interface UseChartRenderParams {
    svgRef: RefObject<SVGSVGElement | null>;
    scales: (ChartScales & { timeDomain: [Date, Date] }) | null;
    layout: ChartLayout;
    theme: Theme;
    // Data
    parsedData: ParsedChartPoint[];
    analysisData: MarketAnalysisData | null | undefined;
    aggregatedVolume: AggregatedVolumePoint[];
    // Config
    isFullSeriesMode: boolean;
    seriesVisibility: SeriesVisibility | undefined;
    volumeVisibility: VolumeVisibility;
    useDecimalScale: boolean;
    isLogScale: boolean;
    // Markets
    markets: MarketMetadata[];
    marketColors: string[];
    // Handlers
    onDateRangeChange?: (startDate: Date, endDate: Date) => void;
    // Frequency
    candleFrequencyMs?: number;
}

export function useChartRender({
    svgRef,
    scales,
    layout,
    theme,
    parsedData,
    analysisData,
    aggregatedVolume,
    isFullSeriesMode,
    seriesVisibility,
    volumeVisibility,
    useDecimalScale,
    isLogScale,
    markets,
    marketColors,
    onDateRangeChange,
    candleFrequencyMs,
}: UseChartRenderParams): void {
    useEffect(() => {
        if (!svgRef.current) return;

        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove();

        const g = svg.append('g')
            .attr('transform', `translate(${layout.margin.left},${layout.margin.top})`);

        // Add clip-path
        const clipId = `chart-clip-${Math.random().toString(36).slice(2, 11)}`;
        svg.append('defs')
            .append('clipPath')
            .attr('id', clipId)
            .append('rect')
            .attr('x', 0)
            .attr('y', 0)
            .attr('width', layout.innerWidth)
            .attr('height', layout.totalChartHeight);

        // If no scales (no data), render empty axes
        if (!scales) {
            renderEmptyAxes(g, layout.innerWidth, layout.priceHeight, useDecimalScale, theme);
            return;
        }

        // Create render context
        const ctx: RenderContext = {
            g,
            scales,
            layout,
            theme,
            clipId,
            candleFrequencyMs,
        };

        // Render Y axis
        renderYAxis(ctx, { isLogScale, useDecimalScale, timeDomain: scales.timeDomain });

        // Render grid lines
        renderGridLines(ctx);

        // Render based on mode
        if (isFullSeriesMode && analysisData) {
            const visibility = seriesVisibility || {
                bidLine: true,
                askLine: true,
                fairValueLine: true,
                uncertaintyBand: true,
                trades: true,
                candles: false,
            };
            renderFullSeriesMode(ctx, { analysisData, visibility });
        } else {
            renderSimpleMode(ctx, { parsedData, markets, marketColors });
        }

        // Render volume panel
        if (layout.showVolume && analysisData?.candles) {
            renderVolumeYAxis(ctx);
            renderVolumePanel(ctx, {
                candles: analysisData.candles,
                aggregatedVolume,
                volumeVisibility,
            });
        }

        // Render X axis (after volume panel so it's at the bottom)
        renderXAxis(ctx, { isLogScale, useDecimalScale, timeDomain: scales.timeDomain });

        // Setup tooltips
        const tooltipElements = createTooltipElements(g, layout, theme);
        const tooltipHandlers = createTooltipHandlers(
            g,
            tooltipElements,
            { scales, layout, theme, useDecimalScale },
            {
                parsedData,
                analysisData: analysisData || null,
                aggregatedVolume,
                markets,
                isFullSeriesMode,
                seriesVisibility,
                volumeVisibility,
            }
        );

        // Setup interactions (brush or simple overlay)
        if (onDateRangeChange) {
            setupBrush(g, { scales, layout, theme, onDateRangeChange }, tooltipHandlers);
        } else {
            setupInteractiveOverlay(g, layout, tooltipHandlers, false);
        }
    }, [
        svgRef,
        scales,
        layout,
        theme,
        parsedData,
        analysisData,
        aggregatedVolume,
        isFullSeriesMode,
        seriesVisibility,
        volumeVisibility,
        useDecimalScale,
        isLogScale,
        markets,
        marketColors,
        onDateRangeChange,
        candleFrequencyMs,
    ]);
}
